import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Info, AlertTriangle, ExternalLink } from 'lucide-react';
import { api } from '../api';
import { PageHeader, Card, SectionTitle, Spinner, Empty } from '../ui';

const AREA_LABEL = { all: 'Generale', financial: 'Financial Risk', compliance: 'HR & Compliance', security: 'Sicurezza', cyber: 'Cyber Security', aml_api: 'API AML' };
const src = (u) => (u.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function Note({ n }) {
  const warn = n.kind === 'attenzione';
  const Icon = warn ? AlertTriangle : Info;
  return (
    <div className={`flex gap-3 p-3 border-l-2 text-sm ${warn ? 'border-[#E5484D] bg-[#E5484D]/[0.07] text-[#E37A72]' : 'border-[#6FA8DC] bg-[#6FA8DC]/[0.10] text-[#C9CCD1]'}`} data-testid={`guide-note-${n.kind}`}>
      <Icon size={16} className="shrink-0 mt-0.5" /><p><b className="uppercase text-[11px] tracking-wider mr-2">{warn ? 'Attenzione' : 'Nota'}</b>{n.text}</p>
    </div>
  );
}

function Media({ media }) {
  const images = media.filter((m) => m.kind === 'image');
  const links = media.filter((m) => m.kind === 'link');
  return (
    <>
      {images.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-4 mt-5">
          {images.map((m, i) => (
            <figure key={i} className="border border-[#1F242C] overflow-hidden" data-testid="guide-image">
              <a href={src(m.url)} target="_blank" rel="noreferrer"><img src={src(m.url)} alt={m.caption || 'Esempio'} loading="lazy" className="w-full h-auto block" /></a>
              {m.caption && <figcaption className="px-3 py-2 text-xs text-[#8A8F97]">{m.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
      {links.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {links.map((m, i) => <a key={i} href={src(m.url)} target="_blank" rel="noreferrer" className="h-9 px-3 text-sm flex items-center gap-2 border border-[#1F242C] text-[#D4AF37] hover:underline" data-testid="guide-link"><ExternalLink size={14} />{m.caption || m.url}</a>)}
        </div>
      )}
    </>
  );
}

function Section({ s }) {
  return (
    <Card className="p-6" data-testid={`guide-section-${s.id}`}>
      <SectionTitle><span className="inline-flex items-center gap-2"><BookOpen size={15} className="text-[#D4AF37]" />{s.title}</span></SectionTitle>
      {s.text && <p className="text-sm text-[#C9CCD1] whitespace-pre-line mb-3">{s.text}</p>}
      {s.items.length > 0 && <ol className="space-y-3 text-sm text-[#C9CCD1] list-decimal pl-5 mb-4">{s.items.map((t) => <li key={t}>{t}</li>)}</ol>}
      {s.notes.length > 0 && <div className="space-y-2">{s.notes.map((n, i) => <Note key={i} n={n} />)}</div>}
      <Media media={s.media} />
    </Card>
  );
}

export default function GuidePage() {
  const [sections, setSections] = useState(null);
  const [area, setArea] = useState(null);
  useEffect(() => { api.get('/guide').then((r) => setSections(r.data)).catch(() => setSections([])); }, []);
  const areas = useMemo(() => [...new Set((sections || []).map((s) => s.area))], [sections]);
  const current = area && areas.includes(area) ? area : areas[0];
  if (!sections) return <Spinner />;
  return (
    <div>
      <PageHeader kicker="Istruzioni per la consultazione" title="Guida">
        {areas.length > 1 && areas.map((a) => (
          <button key={a} onClick={() => setArea(a)} data-testid={`guide-area-${a}`} className={`h-10 px-4 text-sm border rounded-sm transition-colors ${a === current ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#1F242C] text-[#8A8F97] hover:text-[#F2F2F2]'}`}>{AREA_LABEL[a] || a}</button>
        ))}
      </PageHeader>
      {sections.length === 0 ? <Card><Empty icon={BookOpen} title="Nessuna istruzione disponibile" text="La guida per i servizi attivi sul tuo account sarà pubblicata a breve." /></Card> : (
        <div className="space-y-6">{sections.filter((s) => s.area === current).map((s) => <Section key={s.id} s={s} />)}</div>
      )}
    </div>
  );
}
